import { ComponentType } from '@grafikr/components';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import repaint from 'utils/repaint';
import transitionEnd from 'utils/transition-end';
import clear from 'utils/clear';
import { disablePageScroll, enablePageScroll } from 'scroll-lock';

import { ClerkSearchResponse } from '../../typescript/Clerk';

const component: ComponentType = (node, { emitter }) => {
  const closeBtn = node.querySelector('[data-close-btn]');
  const input = node.querySelector<HTMLInputElement>('[data-input]');
  const output = node.querySelector<HTMLElement>('[data-output]');
  const results = node.querySelector<HTMLElement>('[data-results]');
  const suggestions = node.querySelector<HTMLElement>('[data-suggestions]');
  const xProductsFound = node.querySelector('[data-x-products-found]');

  const NODE_ACTIVE_CLASS = 'live-search--active';
  const BODY_ACTIVE_CLASS = 'live-search-active';

  const initHeight = () => {
    let previousWidth = window.innerWidth;

    const update = () => {
      const isHidden = node.hidden;
      node.hidden = false;
      repaint(node);
      document.documentElement.style.setProperty(
        '--live-search-height',
        `${node.getBoundingClientRect().height}px`,
      );
      node.hidden = isHidden;

      previousWidth = window.innerWidth;
    };

    window.addEventListener(
      'resize',
      throttle(() => {
        // only on width change, so as not to fire on height change caused by UI keyboard etc.
        if (window.innerWidth !== previousWidth) update();
      }, 300),
    );

    emitter.on('live-search:updated', update);

    update();
  };

  const initState = () => {
    const open = () => {
      node.hidden = false;
      repaint(node);
      document.body.classList.add(BODY_ACTIVE_CLASS);
      node.classList.add(NODE_ACTIVE_CLASS);
      input.focus();
      disablePageScroll(node);
    };

    const close = () => {
      document.body.classList.remove(BODY_ACTIVE_CLASS);
      node.classList.remove(NODE_ACTIVE_CLASS);

      transitionEnd(node, () => {
        node.hidden = true;
        enablePageScroll(node);

        input.value = '';
        input.dispatchEvent(new Event('keyup'));
      });
    };

    emitter.on('live-search:open', open);
    closeBtn.addEventListener('click', close);
  };

  const initSearch = () => {
    const handleResponse = async (data: ClerkSearchResponse) => {
      const handles = data.product_data.map((item) => item.handle);

      const htmlItems = await Promise.all(
        handles.map(async (handle) => {
          const url = new URL(window.location.origin);
          url.pathname = `/products/${handle}`;
          url.searchParams.set('view', 'item');

          const response = await fetch(url);

          if (response.status >= 200 && response.status < 300) {
            return response.text();
          }

          throw new Error(
            `Could not fetch URL: "${url}". Server responded with: ${response.status}`,
          );
        }),
      );

      xProductsFound.textContent = theme.strings.search[
        data.hits > 1 ? 'xProductsFoundPlural' : 'xProductFoundSingle'
      ].replace('{{ count }}', data.hits.toString());

      clear(output);
      output.insertAdjacentHTML('beforeend', htmlItems.join(''));
      Clerk("click", "*[data-clerk-product-id]");

      suggestions.hidden = data.hits > 0;
      results.hidden = data.hits === 0;

      emitter.emit('live-search:updated');
    };

    input.addEventListener(
      'keyup',
      debounce(() => {
        Clerk(
          'call',
          'search/predictive',
          {
            query: input.value,
            limit: 3,
            attributes: ['handle'],
            labels: ['Live search'],
          },
          (response: ClerkSearchResponse) => handleResponse(response),
          // eslint-disable-next-line no-console
          (response: ClerkSearchResponse) => console.error(response.message),
        );
      }, 300),
    );
  };

  const init = () => {
    initHeight();
    initState();
    initSearch();
  };

  init();
};

export default component;
